import type { QueryClient, QueryObserverOptions } from '@tanstack/query-core';
import type { AxiosInstance } from 'axios';
import { getKiasiBarsSummary, getKiasiVolumeProfile } from 'src/lib/kiasi';
import type {
    KiasiAdjustmentMode,
    KiasiHistorySummaryReadReport,
    KiasiInterval,
    KiasiProfileMode,
    KiasiVolumeProfile,
} from 'src/lib/kiasi';

const VOLUME_PROFILE = 'kiasi-volume-profile';
const VOLUME_BARS_SUMMARY = 'kiasi-volume-bars-summary';

export interface VolumeProfileQueryWindow {
    readonly fromMs: number;
    readonly toMs: number;
}

export interface VolumeProfileQueryInput {
    readonly api: AxiosInstance;
    readonly symbol: string;
    /**
     * Identifies the window in the query key. The window itself is resolved
     * inside `queryFn` so a sliding range follows the clock on every refetch
     * without creating a new cache entry each time.
     */
    readonly rangeKey: string;
    readonly resolveWindow: () => VolumeProfileQueryWindow;
    readonly mode: KiasiProfileMode;
    readonly adjust?: KiasiAdjustmentMode;
    readonly enabled: boolean;
    readonly refetchIntervalMs?: number | false;
}

/**
 * Aggregated profile over the one-minute bars in the window. One-minute bars
 * are always requested so every range, sliding or not, aggregates the same
 * closed bars that the live stream's open bar extends (PLAT-12347).
 */
export function getVolumeProfileQuery({
    api,
    symbol,
    rangeKey,
    resolveWindow,
    mode,
    adjust,
    enabled,
    refetchIntervalMs = false,
}: VolumeProfileQueryInput): QueryObserverOptions<KiasiVolumeProfile, Error, KiasiVolumeProfile> {
    return {
        queryKey: [VOLUME_PROFILE, symbol, rangeKey, mode, adjust ?? 'default'] as readonly unknown[],
        queryFn: ({ signal }) => {
            const { fromMs, toMs } = resolveWindow();
            return getKiasiVolumeProfile(
                api,
                { symbol, interval: '1m', fromMs, toMs, mode, ...(adjust !== undefined ? { adjust } : {}) },
                { signal },
            );
        },
        enabled: enabled && symbol !== '',
        staleTime: 0,
        // The app default retries 5xx indefinitely; a profile that cannot load should surface quickly.
        retry: 1,
        refetchInterval: refetchIntervalMs,
    };
}

export interface VolumeBarsSummaryQueryInput {
    readonly api: AxiosInstance;
    readonly symbol: string;
    readonly interval: KiasiInterval;
    readonly rangeKey: string;
    readonly resolveWindow: () => VolumeProfileQueryWindow;
    readonly adjust?: KiasiAdjustmentMode;
    readonly enabled: boolean;
    readonly refetchIntervalMs?: number | false;
}

/** Bars without histograms over the window: candles, per-bar volume, and auction volumes for the chart. */
export function getVolumeBarsSummaryQuery({
    api,
    symbol,
    interval,
    rangeKey,
    resolveWindow,
    adjust,
    enabled,
    refetchIntervalMs = false,
}: VolumeBarsSummaryQueryInput): QueryObserverOptions<
    KiasiHistorySummaryReadReport,
    Error,
    KiasiHistorySummaryReadReport
> {
    return {
        queryKey: [VOLUME_BARS_SUMMARY, symbol, interval, rangeKey, adjust ?? 'default'] as readonly unknown[],
        queryFn: ({ signal }) => {
            const { fromMs, toMs } = resolveWindow();
            return getKiasiBarsSummary(
                api,
                { symbol, interval, fromMs, toMs, ...(adjust !== undefined ? { adjust } : {}) },
                { signal },
            );
        },
        enabled: enabled && symbol !== '',
        staleTime: 0,
        retry: 1,
        refetchInterval: refetchIntervalMs,
    };
}

export function invalidateVolumeProfileQueries(queryClient: QueryClient): void {
    void queryClient.invalidateQueries({ queryKey: [VOLUME_PROFILE] });
    void queryClient.invalidateQueries({ queryKey: [VOLUME_BARS_SUMMARY] });
}
